/**
 * Typed WebSocket client wrapper (06 §0 "Realtime").
 *
 * Two modes, switched by `VITE_API_BASE` (see `session.ts`):
 *   - **live** → POST `/v1/realtime/ticket`, then open a native `WebSocket` to
 *     the UserHub Durable Object at `${wsBase}/v1/realtime/ws?ticket=…&device_id=…`.
 *     Inbound text frames are validated with `parseWsFrame`; outbound frames are
 *     `JSON.stringify`'d.
 *   - **backendless** → wraps `mockTransport`.
 *
 * Either way it speaks the @sinyal/contracts frame catalog, and the public
 * surface (`start`/`stop`/`onFrame`/`onState`/`sendMessage`/…`) is identical, so
 * callers (e.g. `chatStore`) never branch on transport.
 */

import type {
  ClientToServerFrame,
  MsgSendFrame,
  ServerToClientFrame,
  WsContentType,
} from '@sinyal/contracts';
import { WS_PROTOCOL_VERSION, makeEnvelope, parseWsFrame } from '@sinyal/contracts';
import { seal, type PlaintextBody } from './codec';
import { ulid } from './ids';
import { mockTransport } from './mockTransport';
import { restClient } from './restClient';
import { IS_LIVE, WS_BASE, getDeviceId } from './session';

export type ConnectionState = 'connecting' | 'online' | 'reconnecting' | 'offline';

type FrameHandler = (frame: ServerToClientFrame) => void;
type StateHandler = (state: ConnectionState) => void;

/** How the active connection actually moves bytes. */
interface Transport {
  /** Begin connecting; call `onFrame`/`onState` as events arrive. */
  connect(handlers: {
    onFrame: (frame: ServerToClientFrame) => void;
    onState: (state: ConnectionState) => void;
  }): void;
  /** Send a client→server frame. */
  send(frame: ClientToServerFrame): void;
  /** Tear down. */
  close(): void;
}

/* -------------------------------------------------------------------------- */
/*  Mock transport adapter                                                     */
/* -------------------------------------------------------------------------- */

class MockTransportAdapter implements Transport {
  private unsub: (() => void) | null = null;

  connect(handlers: {
    onFrame: (frame: ServerToClientFrame) => void;
    onState: (state: ConnectionState) => void;
  }): void {
    handlers.onState('connecting');
    this.unsub = mockTransport.onFrame((frame) => {
      if (frame.t === 'conn.ready') handlers.onState('online');
      handlers.onFrame(frame);
    });
    mockTransport.connect();
  }

  send(frame: ClientToServerFrame): void {
    // The mock takes the object directly; a real socket JSON.stringify's it.
    mockTransport.send(frame);
  }

  close(): void {
    this.unsub?.();
    this.unsub = null;
  }
}

/* -------------------------------------------------------------------------- */
/*  Live WebSocket transport                                                   */
/* -------------------------------------------------------------------------- */

class WebSocketTransport implements Transport {
  private socket: WebSocket | null = null;
  private closed = false;
  private retries = 0;
  private reconnectTimer: ReturnType<typeof setTimeout> | null = null;
  private handlers: {
    onFrame: (frame: ServerToClientFrame) => void;
    onState: (state: ConnectionState) => void;
  } | null = null;

  connect(handlers: {
    onFrame: (frame: ServerToClientFrame) => void;
    onState: (state: ConnectionState) => void;
  }): void {
    this.handlers = handlers;
    this.closed = false;
    void this.open(false);
  }

  private async open(isRetry: boolean): Promise<void> {
    if (this.closed || !this.handlers) return;
    this.handlers.onState(isRetry ? 'reconnecting' : 'connecting');

    const deviceId = getDeviceId();
    let ticket: string;
    try {
      const res = await restClient.realtimeTicket({ device_id: deviceId });
      ticket = res.ticket;
    } catch {
      this.scheduleReconnect();
      return;
    }
    if (this.closed) return;

    const url =
      `${WS_BASE}/v1/realtime/ws` +
      `?ticket=${encodeURIComponent(ticket)}` +
      `&device_id=${encodeURIComponent(deviceId)}`;

    const ws = new WebSocket(url);
    this.socket = ws;

    ws.addEventListener('message', (ev: MessageEvent) => {
      if (typeof ev.data !== 'string') return; // protocol is text JSON frames
      const parsed = parseWsFrame(ev.data);
      if (!parsed.ok) {
        // Drop malformed inbound frames rather than crashing the UI.
        return;
      }
      const frame = parsed.data as ServerToClientFrame;
      if (frame.t === 'conn.ready') {
        this.retries = 0;
        this.handlers?.onState('online');
      }
      this.handlers?.onFrame(frame);
    });

    ws.addEventListener('close', () => {
      if (this.closed) return;
      this.scheduleReconnect();
    });

    ws.addEventListener('error', () => {
      // `close` fires after `error`; let it drive the reconnect.
      try {
        ws.close();
      } catch {
        /* ignore */
      }
    });
  }

  private scheduleReconnect(): void {
    if (this.closed || !this.handlers) return;
    this.socket = null;
    this.handlers.onState('reconnecting');
    const backoff = Math.min(30_000, 1_000 * 2 ** this.retries);
    this.retries += 1;
    this.reconnectTimer = setTimeout(() => void this.open(true), backoff);
  }

  send(frame: ClientToServerFrame): void {
    if (this.socket && this.socket.readyState === WebSocket.OPEN) {
      this.socket.send(JSON.stringify(frame));
    }
    // If the socket isn't open, the frame is dropped; chatStore retries on
    // reconnect via resync. (Outbound buffering is out of scope for this seam.)
  }

  close(): void {
    this.closed = true;
    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }
    if (this.socket) {
      try {
        this.socket.close();
      } catch {
        /* ignore */
      }
      this.socket = null;
    }
    this.handlers?.onState('offline');
  }
}

/* -------------------------------------------------------------------------- */
/*  Client facade                                                              */
/* -------------------------------------------------------------------------- */

class WsClient {
  private frameHandlers = new Set<FrameHandler>();
  private stateHandlers = new Set<StateHandler>();
  private state: ConnectionState = 'connecting';
  private transport: Transport | null = null;
  private started = false;

  start(): void {
    if (this.started) return;
    this.started = true;
    this.setState('connecting');
    this.transport = IS_LIVE ? new WebSocketTransport() : new MockTransportAdapter();
    this.transport.connect({
      onFrame: (frame) => this.dispatch(frame),
      onState: (s) => this.setState(s),
    });
  }

  stop(): void {
    this.transport?.close();
    this.transport = null;
    this.started = false;
  }

  getState(): ConnectionState {
    return this.state;
  }

  onFrame(fn: FrameHandler): () => void {
    this.frameHandlers.add(fn);
    return () => this.frameHandlers.delete(fn);
  }

  onState(fn: StateHandler): () => void {
    this.stateHandlers.add(fn);
    fn(this.state);
    return () => this.stateHandlers.delete(fn);
  }

  private setState(s: ConnectionState): void {
    this.state = s;
    for (const h of this.stateHandlers) h(s);
  }

  private dispatch(frame: ServerToClientFrame): void {
    for (const h of this.frameHandlers) h(frame);
  }

  private rawSend(frame: ClientToServerFrame): void {
    this.transport?.send(frame);
  }

  /** Build + send a `msg.send`, sealing the plaintext into `ciphertext`. */
  sendMessage(args: {
    cid: string;
    clientMsgId: string;
    contentType: WsContentType;
    body: PlaintextBody;
    replyToSeq?: number;
    mediaId?: string;
  }): MsgSendFrame {
    const env = makeEnvelope({ t: 'msg.send', id: ulid(), cid: args.cid });
    const frame: MsgSendFrame = {
      ...env,
      cid: args.cid,
      client_msg_id: args.clientMsgId,
      content_type: args.contentType,
      ciphertext: seal(args.body),
      ratchet_header: seal({ text: '', content_type: 'system' }),
      ...(args.replyToSeq !== undefined ? { reply_to_seq: args.replyToSeq } : {}),
      ...(args.mediaId !== undefined ? { media_id: args.mediaId } : {}),
    };
    this.rawSend(frame);
    return frame;
  }

  sendTyping(cid: string, on: boolean): void {
    const t = on ? 'typing.start' : 'typing.stop';
    this.rawSend({
      t,
      v: WS_PROTOCOL_VERSION,
      id: ulid(),
      ts: Date.now(),
      cid,
    } as ClientToServerFrame);
  }

  sendRead(cid: string, upToSeq: number): void {
    this.rawSend({
      t: 'msg.read',
      v: WS_PROTOCOL_VERSION,
      id: ulid(),
      ts: Date.now(),
      cid,
      up_to_seq: upToSeq,
    });
  }

  sendDelivered(cid: string, upToSeq: number): void {
    this.rawSend({
      t: 'msg.delivered',
      v: WS_PROTOCOL_VERSION,
      id: ulid(),
      ts: Date.now(),
      cid,
      up_to_seq: upToSeq,
    });
  }
}

export const wsClient = new WsClient();
